import axios from "axios";

// Normalize API base URL to always end with /api
let API_BASE_URL =
	process.env.REACT_APP_API_BASE_URL ||
	"https://heliocentric-astrology-application-a78b.onrender.com";
if (!API_BASE_URL.endsWith("/api")) {
	// Remove trailing slash if present, then add /api
	API_BASE_URL = API_BASE_URL.replace(/\/$/, "") + "/api";
}

const client = axios.create({
	baseURL: API_BASE_URL,
	timeout: 30000,
});

export const setAuthToken = (token) => {
	if (token) {
		client.defaults.headers.common.Authorization = `Bearer ${token}`;
	} else {
		delete client.defaults.headers.common.Authorization;
	}
};
// Request cancellation tokens map
const cancelTokens = new Map();

/**
 * Cancel a pending request
 * @param {string} requestId - Unique request identifier
 */
export const cancelRequest = (requestId) => {
	const cancelToken = cancelTokens.get(requestId);
	if (cancelToken) {
		cancelToken.cancel(`Request ${requestId} was cancelled`);
		cancelTokens.delete(requestId);
	}
};
export const googleLogin = async (payload) => {
	const { data } = await client.post("/auth/google-login", payload);
	return data;
};

export const appleLogin = async (payload) => {
	const { data } = await client.post("/auth/apple-login", payload);
	return data;
};

/**
 * Create a cancellable request
 * @param {string} requestId - Unique request identifier
 * @param {Function} requestFn - Function that makes the axios request
 * @returns {Promise} Request promise
 */
const makeCancellableRequest = (requestId, requestFn) => {
	// Cancel previous request with same ID if exists
	cancelRequest(requestId);

	const source = axios.CancelToken.source();
	cancelTokens.set(requestId, source);

	try {
		return requestFn(source.token).finally(() => {
			cancelTokens.delete(requestId);
		});
	} catch (error) {
		cancelTokens.delete(requestId);
		throw error;
	}
};

/**
 * Retry a request with exponential backoff
 * @param {Function} requestFn - Function that makes the request
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} Request promise
 */
const retryRequest = async (requestFn, maxRetries = 3, baseDelay = 1000) => {
	let lastError;
	for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
		try {
			return await requestFn();
		} catch (error) {
			lastError = error;

			// Don't retry on client errors (4xx) including 429 (rate limit)
			if (error.status && error.status >= 400 && error.status < 500) {
				throw error;
			}

			// Don't retry on cancellation
			if (axios.isCancel(error)) {
				throw error;
			}

			// Don't retry on last attempt
			if (attempt >= maxRetries) {
				throw error;
			}

			// Calculate delay with exponential backoff
			const delay = baseDelay * Math.pow(2, attempt);
			await new Promise((resolve) => setTimeout(resolve, delay));
		}
	}
	throw lastError;
};

// Request interceptor for error handling
client.interceptors.request.use(
	(config) => {
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

// Response interceptor for error handling
client.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => {
		// Handle different error types
		if (error.response) {
			// Server responded with error status
			const { status, data } = error.response;
			const message =
				data?.message || data?.error || `Request failed with status ${status}`;

			// Create error object with useful information
			const apiError = new Error(message);
			apiError.status = status;
			apiError.data = data;
			apiError.isApiError = true;

			return Promise.reject(apiError);
		} else if (error.request) {
			// Request was made but no response received
			const networkError = new Error(
				"Network error: Unable to reach server. Please check your connection.",
			);
			networkError.isNetworkError = true;
			return Promise.reject(networkError);
		} else {
			// Something else happened
			return Promise.reject(error);
		}
	},
);

/**
 * Calculate chart from birth data (legacy - uses single call)
 * @param {Object} payload - Birth data payload
 * @param {string} [requestId] - Optional request ID for cancellation
 * @returns {Promise<Object>} Chart data
 */
export const calculateChart = async (payload, requestId) => {
	const requestFn = requestId
		? (cancelToken) => client.post("/chart/calculate", payload, { cancelToken })
		: () => client.post("/chart/calculate", payload);

	const executeRequest = requestId
		? () => makeCancellableRequest(requestId, requestFn)
		: () => retryRequest(requestFn);

	try {
		const { data } = await executeRequest();
		return data;
	} catch (error) {
		if (axios.isCancel(error)) {
			throw new Error("Request was cancelled");
		}
		throw error;
	}
};

/**
 * Calculate basic chart (fast - SwissEph planets only)
 * @param {Object} payload - Birth data payload
 * @param {string} [requestId] - Optional request ID for cancellation
 * @returns {Promise<Object>} Basic chart data
 */
export const calculateBasicChart = async (payload, requestId) => {
	const requestFn = requestId
		? (cancelToken) =>
				client.post("/chart/calculate-basic", payload, { cancelToken })
		: () => client.post("/chart/calculate-basic", payload);

	const executeRequest = requestId
		? () => makeCancellableRequest(requestId, requestFn)
		: () => retryRequest(requestFn);

	try {
		const { data } = await executeRequest();
		return data;
	} catch (error) {
		if (axios.isCancel(error)) {
			throw new Error("Request was cancelled");
		}
		throw error;
	}
};

/**
 * Calculate dwarf planets and merge with existing chart
 * @param {Object} payload - Birth data payload with existingPlanets
 * @param {string} [requestId] - Optional request ID for cancellation
 * @returns {Promise<Object>} Dwarf planets data
 */
export const calculateDwarfChart = async (payload, requestId) => {
	const requestFn = requestId
		? (cancelToken) =>
				client.post("/chart/calculate-dwarf", payload, { cancelToken })
		: () => client.post("/chart/calculate-dwarf", payload);

	const executeRequest = requestId
		? () => makeCancellableRequest(requestId, requestFn)
		: () => retryRequest(requestFn);

	try {
		const { data } = await executeRequest();
		return data;
	} catch (error) {
		if (axios.isCancel(error)) {
			throw new Error("Request was cancelled");
		}
		throw error;
	}
};

/**
 * Generate interpretations for a chart
 * @param {Object} payload - Interpretation request payload
 * @param {string} [requestId] - Optional request ID for cancellation
 * @returns {Promise<Object>} Interpretation data
 */
export const generateInterpretations = async (payload, requestId) => {
	const requestFn = requestId
		? (cancelToken) =>
				client.post("/interpretations/generate", payload, { cancelToken })
		: () => client.post("/interpretations/generate", payload);

	const executeRequest = requestId
		? () => makeCancellableRequest(requestId, requestFn)
		: () => retryRequest(requestFn);

	try {
		const { data } = await executeRequest();
		return data;
	} catch (error) {
		if (axios.isCancel(error)) {
			throw new Error("Request was cancelled");
		}
		throw error;
	}
};

/**
 * Generate batch interpretations for all chart elements in a single call
 * @param {Object} payload - Batch interpretation request payload
 * @param {string} [requestId] - Optional request ID for cancellation
 * @returns {Promise<Object>} Batch interpretation data
 */
export const generateBatchInterpretations = async (payload, requestId) => {
	const requestFn = requestId
		? (cancelToken) =>
				client.post("/interpretations/generate-batch", payload, { cancelToken })
		: () => client.post("/interpretations/generate-batch", payload);

	const executeRequest = requestId
		? () => makeCancellableRequest(requestId, requestFn)
		: () => retryRequest(requestFn);

	try {
		const { data } = await executeRequest();
		return data;
	} catch (error) {
		if (axios.isCancel(error)) {
			throw new Error("Request was cancelled");
		}
		throw error;
	}
};

export const createCheckoutSession = async (payload) => {
	const response = client.post("/payment/create-checkout-session", payload);
	return response;
};

/**
 * Generate a transit interpretation
 * @param {Object} payload - Transit interpretation payload
 * @returns {Promise<Object>} Transit interpretation
 */
export const generateTransitInterpretation = async (payload) => {
	const { data } = await client.post("/interpretations/transit", payload);
	return data;
};

/**
 * Generate a synastry interpretation
 * @param {Object} payload - Synastry interpretation payload
 * @returns {Promise<Object>} Synastry interpretation
 */
export const generateSynastryInterpretation = async (payload) => {
	const { data } = await client.post("/interpretations/synastry", payload);
	return data;
};

/**
 * Submit a user rating for an interpretation
 * @param {Object} payload - Rating payload
 * @returns {Promise<Object>} Rating summary
 */
export const submitInterpretationRating = async (payload) => {
	const { data } = await client.post("/interpretations/feedback", payload);
	return data;
};

/**
 * Fetch rating summary for a chart hash and scope
 * @param {Object} params - Query params
 * @returns {Promise<Object>} Rating summary
 */
export const fetchRatingSummary = async (params) => {
	const { data } = await client.get("/interpretations/feedback/summary", {
		params,
	});
	return data;
};

/**
 * Load interpretations for a chart (deprecated in stateless mode)
 * @param {string} chartId - Chart ID
 * @returns {Promise<Object>} Interpretations data
 */
export const loadInterpretations = async (chartId) => {
	try {
		const { data } = await client.get(`/interpretations/${chartId}`);
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * Fetch application settings (orbs and AI providers)
 * @returns {Promise<Object>} Settings data
 */
export const fetchSettings = async () => {
	try {
		const [orbs, providers, costs] = await Promise.all([
			client.get("/settings/orbs"),
			client.get("/settings/ai-providers"),
			client.get("/settings/costs/summary"),
		]);

		return {
			orbs: orbs.data,
			providers: providers.data,
			costs: costs.data,
		};
	} catch (error) {
		throw error;
	}
};

/**
 * Update orb settings
 * @param {Object} payload - Orb settings payload
 * @returns {Promise<Object>} Updated settings
 */
export const updateOrbSettings = async (payload) => {
	try {
		const { data } = await client.put("/settings/orbs", payload);
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * Set default AI provider
 * @param {Object} payload - Provider settings payload
 * @returns {Promise<Object>} Success response
 */
export const setDefaultProvider = async (payload) => {
	try {
		const { data } = await client.put("/settings/ai-provider", payload);
		return data;
	} catch (error) {
		throw error;
	}
};

export const signupUser = async (payload) => {
	const { data } = await client.post("/auth/signup", payload);
	return data;
};

export const loginUser = async (payload) => {
	const { data } = await client.post("/auth/login", payload);
	return data;
};

export const fetchCurrentUser = async () => {
	const { data } = await client.get("/auth/me");
	return data;
};

export const fetchFreePlanetsStatus = async () => {
	const { data } = await client.get("/auth/me/free-planets-status");
	return data;
};

export const fetchFreeAspectsStatus = async () => {
	const { data } = await client.get("/auth/me/free-aspects-status");
	return data;
};

export const unlockChart = async (birthData) => {
	const { data } = await client.post("/chart/unlock", { birthData });
	return data;
};

export const fetchUnlockedCharts = async () => {
	const { data } = await client.get("/chart/unlocked");
	return data;
};

export const fetchOrderStatus = async (orderId) => {
	const { data } = await client.get(`/payment/status/${orderId}`);
	return data;
};

export const fetchOrderBySession = async (sessionId) => {
	const { data } = await client.get(`/payment/order/session/${sessionId}`);
	return data;
};

/**
 * Fetch prepared report HTML for a given order.
 * The backend generates this HTML using the same template that was
 * previously rendered for Puppeteer, so the PDF can match exactly.
 */
export const fetchReportHtml = async (orderId) => {
	const { data } = await client.get(`/reports/${orderId}/html`);
	return data;
};

/**
 * Upload a client-generated PDF to the backend so it can be stored
 * in Cloudinary and emailed to the user.
 *
 * @param {string} orderId
 * @param {Blob} pdfBlob
 */
export const uploadReportPdf = async (orderId, pdfBlob) => {
	const { data } = await client.post(
		`/reports/${orderId}/upload-pdf`,
		pdfBlob,
		{
			headers: {
				"Content-Type": "application/pdf",
			},
		},
	);
	return data;
};

/**
 * Fetch payment history for a user.
 * @param {string} userId - User ID
 * @param {Object} [options] - Optional pagination (backward compatible with older API)
 * @param {number} [options.page] - Page number (1-based)
 * @param {number} [options.limit] - Items per page
 */
export const fetchPaymentHistory = async (userId, options = {}) => {
	const { page, limit } = options;
	const params = {};
	if (typeof page === "number" && page >= 1) params.page = page;
	if (typeof limit === "number" && limit >= 1) params.limit = limit;
	const query = Object.keys(params).length
		? "?" + new URLSearchParams(params).toString()
		: "";
	const { data } = await client.get(`/payment/history/${userId}${query}`);
	return data;
};

/**
 * Fetch subscription status for the current user
 * @returns {Promise<Object>} Subscription status data
 */
export const fetchSubscriptionStatus = async () => {
	const { data } = await client.get("/payment/subscription/status");
	return data;
};

/**
 * Cancel subscription (future implementation)
 * @returns {Promise<Object>} Cancellation response
 */
export const cancelSubscription = async () => {
	const { data } = await client.post("/payment/subscription/cancel");
	return data;
};

/**
 * Renew subscription
 * @returns {Promise<Object>} Renewal response
 */
export const renewSubscription = async () => {
	const { data } = await client.post("/payment/subscription/renew");
	return data;
};

export const verifyEmail = async (payload) => {
	const { data } = await client.post("/auth/verify-email", payload);
	return data;
};

export const resendOtp = async (payload) => {
	const { data } = await client.post("/auth/resend-otp", payload);
	return data;
};

export const forgotPassword = async (payload) => {
	const { data } = await client.post("/auth/forgot-password", payload);
	return data;
};

export const resetPassword = async (payload) => {
	const { data } = await client.post("/auth/reset-password", payload);
	return data;
};

const api = {
	calculateChart,
	calculateBasicChart,
	calculateDwarfChart,
	generateInterpretations,
	generateBatchInterpretations,
	generateTransitInterpretation,
	generateSynastryInterpretation,
	submitInterpretationRating,
	fetchRatingSummary,
	loadInterpretations,
	fetchSettings,
	updateOrbSettings,
	setDefaultProvider,
	signupUser,
	loginUser,
	fetchCurrentUser,
	fetchFreePlanetsStatus,
	fetchFreeAspectsStatus,
	fetchOrderStatus,
	fetchOrderBySession,
	verifyEmail,
	resendOtp,
};

export default api;
export const updateProfile = async (data) => {
	const response = await client.put("/auth/me/profile", data);
	return response.data;
};

export const verifyEmailChange = async (otp) => {
	const response = await client.post("/auth/me/verify-email-change", { otp });
	return response.data;
};
